import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import empty_box from "../../images/empty_box.png";
import Client from "../../utils/client";

const Invoices = () => {
    const [selectedStatus, setSelectedStatus] = useState("pending");
    const [invoiceType, setInvoiceType] = useState("received"); // Default to received invoices

    // Fetch invoices based on status and type
    const { data: invoices, isFetching } = useQuery({
        queryKey: ["invoices", selectedStatus, invoiceType],
        queryFn: async () => {
            const response = await Client.get(`/invoices?status=${selectedStatus}&type=${invoiceType}`).then(
                (res) => res.data
            );
            return response;
        },
        initialData: [],
    });

    // Status Badge Styling
    const statusStyles = {
        pending: "bg-yellow-100 text-yellow-600 border-yellow-300",
        accepted: "bg-green-100 text-green-600 border-green-300",
        rejected: "bg-red-100 text-red-600 border-red-300",
    };

    return (
        <div className="w-full lg:px-20 relative">
            <h1 className="text-end font-bold text-2xl lg:text-3xl my-10 text-primary">
                الفواتير
            </h1>

            {/* Invoice Type Selector */}
            <div className="flex justify-end gap-3 mb-4">
                {[
                    { key: "received", label: "الفواتير المستلمة" },
                    { key: "issued", label: "الفواتير المرسلة" },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setInvoiceType(key)}
                        className={`px-4 py-2 rounded-lg border transition-all ${invoiceType === key
                            ? "bg-primary text-white border-primary"
                            : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Status Tabs */}
            <div className="flex justify-end gap-3 mb-6">
                {[
                    { key: "pending", label: "معلقة" },
                    { key: "accepted", label: "مقبولة" },
                    { key: "rejected", label: "مرفوضة" },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setSelectedStatus(key)}
                        className={`px-4 py-2 rounded-lg border transition-all ${selectedStatus === key
                            ? "bg-primary text-white border-primary"
                            : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Invoice List */}
            {isFetching ? (
                <div dir="rtl" className="my-16 grid grid-cols-1 gap-4 px-0 lg:grid-cols-1 lg:mr-8">
                    {[...Array(5).keys()].map((_, i) => (
                        <div key={i} className="flex flex-row-reverse items-center justify-between gap-3 p-4 bg-gray-100 border rounded-xl shadow-sm animate-pulse">
                            <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-300 rounded w-16"></div>
                        </div>
                    ))}
                </div>
            ) : invoices?.length === 0 ? (
                <div className="flex flex-col mt-10 justify-center items-center text-center">
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
                        <img src={empty_box} alt="No Invoices" className="w-40 lg:w-48" />
                    </motion.div>
                    <div className="font-bold mt-3 text-lg text-gray-700">لا توجد فواتير</div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {invoices?.map((invoice) => (
                        <motion.div key={invoice.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                            <Link
                                to={`/home/invoice/${invoice.id}`}
                                className="flex flex-row-reverse items-center justify-between gap-3 p-5 bg-white border rounded-xl shadow-sm transition-all hover:shadow-md hover:scale-[1.02] border-gray-200"
                            >
                                <div className="flex flex-col text-right w-full">
                                    <div className="flex justify-between items-center">
                                        <span className="font-bold text-lg text-gray-800">فاتورة #{invoice.id}</span>
                                        <span className={`px-3 py-1 text-xs font-semibold rounded border ${statusStyles[invoice.status]}`}>
                                            {invoice.status === "pending" ? "معلقة" :
                                                invoice.status === "accepted" ? "مقبولة" : "مرفوضة"}
                                        </span>
                                    </div>


                                    <div className="text-sm text-gray-600 mt-1">
                                        {/* الكمية: {invoice.quantity} |  */}
                                        المبلغ: {invoice.amount} {invoice.currency}
                                    </div>

                                    <div className="text-sm text-gray-400 mt-1">
                                        تاريخ الإصدار: {new Date(invoice.created_at).toLocaleDateString("ar-EG", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </div>

                                    <div className="text-sm text-gray-400 mt-1">
                                        تاريخ الاستحقاق: {new Date(invoice.dueDate).toLocaleDateString("ar-EG", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Invoices;
